import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Search } from '@mui/icons-material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import Task from '../utils/TaskMaster';

const filterTaskTree = (task, normalizedQuery) => {
  if (!normalizedQuery) return task;

  const matchingChildren = (task.subtasks || [])
    .map((subtask) => filterTaskTree(subtask, normalizedQuery))
    .filter(Boolean);

  const isDirectMatch = task.title?.toLocaleLowerCase().includes(normalizedQuery);
  if (!isDirectMatch && !matchingChildren.length) return null;
  return {
    ...task,
    subtasks: matchingChildren,
    hasSearchMatchBelow: !isDirectMatch && matchingChildren.length > 0,
  };
};

const renderRecursiveTasks = (task) => (
  <TreeItem
    key={task.id}
    itemId={task.id}
    label={task.title}
    className={task.hasSearchMatchBelow ? 'search-match-below' : undefined}
  >
    {(task.subtasks || []).map(renderRecursiveTasks)}
  </TreeItem>
);

const TaskSelector = ({
  idPrefix = 'task-selection',
  onTaskSelect,
  taskData,
  selectedTaskId = null,
  searchLabel = 'Search tasks',
}) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const [searchExpandedItems, setSearchExpandedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const normalizedQuery = deferredSearchQuery.trim().toLocaleLowerCase();
  const isSearching = normalizedQuery.length > 0;

  const roots = useMemo(() => {
    if (!Array.isArray(taskData)) return [];
    return taskData.map((task) => new Task(task));
  }, [taskData]);

  const filteredRoots = useMemo(() => {
    return roots
      .map((root) => filterTaskTree(root, normalizedQuery))
      .filter(Boolean);
  }, [roots, normalizedQuery]);

  useEffect(() => {
    if (isSearching) setSearchExpandedItems([]);
  }, [isSearching]);

  const visibleExpandedItems = isSearching ? searchExpandedItems : expandedItems;
  const treeItems = useMemo(
    () => filteredRoots.map(renderRecursiveTasks),
    [filteredRoots],
  );

  return (
    <Box
      id={`${idPrefix}-selector`}
      className="task-selector-tree"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 0,
        pt: 1,
      }}
    >
      <TextField
        id={`${idPrefix}-search`}
        autoFocus
        fullWidth
        size="small"
        label={searchLabel}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            background:
              `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)}, ` +
              `${alpha(theme.palette.background.default, 0.92)} 28%)`,
            transition: 'box-shadow 160ms ease, transform 160ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: alpha(theme.palette.primary.main, 0.72),
            },
            '&:hover': {
              boxShadow: `0 0 16px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            '&.Mui-focused': {
              transform: 'translateY(-1px)',
              boxShadow:
                `0 0 0 1px ${alpha(theme.palette.green?.main || '#39FF14', 0.35)}, ` +
                `0 0 20px ${alpha(theme.palette.primary.main, 0.32)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.green?.main || '#39FF14',
              },
            },
          },
          '& .MuiInputAdornment-root': {
            color: theme.palette.secondary.main,
            filter: `drop-shadow(0 0 5px ${alpha(theme.palette.secondary.main, 0.7)})`,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.green?.main || '#39FF14',
          },
        })}
      />

      <Box
        id={`${idPrefix}-list`}
        sx={(theme) => ({
          position: 'relative',
          minHeight: 180,
          maxHeight: { xs: '42vh', sm: 360 },
          overflowY: 'auto',
          border: `2px solid ${alpha(theme.palette.primary.main, 0.68)}`,
          borderRadius: 2,
          p: 1.25,
          background:
            `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.055)} 0%, ` +
            `${alpha(theme.palette.background.default, 0.92)} 42%, ` +
            `${alpha(theme.palette.secondary.main, 0.045)} 100%)`,
          boxShadow:
            `inset 0 0 24px rgba(0, 0, 0, 0.45), ` +
            `0 0 18px ${alpha(theme.palette.primary.main, 0.12)}`,
          scrollbarColor:
            `${alpha(theme.palette.secondary.main, 0.7)} ${alpha(theme.palette.background.default, 0.75)}`,
          scrollbarWidth: 'thin',
          '&::before': {
            content: '""',
            position: 'sticky',
            display: 'block',
            top: -10,
            left: 0,
            width: '100%',
            height: 2,
            background:
              `linear-gradient(90deg, ${theme.palette.primary.main}, transparent 70%)`,
            boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.7)}`,
            zIndex: 1,
          },
        })}
      >
        {!roots.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No tasks available
          </Typography>
        ) : !filteredRoots.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No tasks match your search
          </Typography>
        ) : (
          <SimpleTreeView
            id={`${idPrefix}-tree`}
            checkboxSelection
            selectedItems={selectedTaskId}
            expandedItems={visibleExpandedItems}
            onExpandedItemsChange={(event, itemIds) => {
              if (isSearching) setSearchExpandedItems(itemIds);
              else setExpandedItems(itemIds);
            }}
            onItemSelectionToggle={(event, itemId, isSelected) => {
              if (isSelected) onTaskSelect(itemId);
            }}
            slots={{
              collapseIcon: ExpandMore,
              expandIcon: ChevronRight,
            }}
            sx={(theme) => ({
              py: 0.5,
              '& .MuiTreeItem-content': {
                minHeight: 42,
                mb: 0.5,
                pr: 1,
                pl: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
                border: '1px solid transparent',
                borderRadius: 1.5,
                color: theme.palette.primary.main,
                transition:
                  'transform 130ms ease, background-color 130ms ease, border-color 130ms ease, box-shadow 130ms ease',
                '&:hover': {
                  transform: 'translateX(3px)',
                  color: theme.palette.green?.main || '#39FF14',
                  borderColor: alpha(theme.palette.primary.main, 0.48),
                  backgroundColor: alpha(theme.palette.primary.main, 0.09),
                  boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.secondary.main, 0.85),
                  background:
                    `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.28)}, ` +
                    `${alpha(theme.palette.primary.main, 0.1)})`,
                  boxShadow:
                    `inset 4px 0 0 ${theme.palette.secondary.main}, ` +
                    `0 0 14px ${alpha(theme.palette.secondary.main, 0.2)}`,
                },
              },
              '& .MuiTreeItem-iconContainer': {
                color: theme.palette.green?.main || '#39FF14',
              },
              '& .search-match-below > .MuiTreeItem-content:not(.Mui-expanded) .MuiTreeItem-iconContainer': {
                animation: 'searchPathBounce 900ms ease-in-out infinite',
                filter:
                  `drop-shadow(0 0 6px ${alpha(theme.palette.green?.main || '#39FF14', 0.75)})`,
              },
              '@keyframes searchPathBounce': {
                '0%, 100%': { transform: 'translateX(0)' },
                '45%': { transform: 'translateX(5px)' },
                '65%': { transform: 'translateX(1px)' },
              },
              '& .MuiTreeItem-label': {
                py: 0.75,
                overflowWrap: 'anywhere',
              },
              '& .MuiTreeItem-checkbox': {
                color: theme.palette.secondary.main,
              },
              '@media (prefers-reduced-motion: reduce)': {
                '& .MuiTreeItem-content': {
                  transition: 'none',
                },
                '& .search-match-below > .MuiTreeItem-content:not(.Mui-expanded) .MuiTreeItem-iconContainer': {
                  animation: 'none',
                },
              },
            })}
          >
            {treeItems}
          </SimpleTreeView>
        )}
      </Box>
    </Box>
  );
};

export default TaskSelector;
